import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import ModalWrapper, { ModalCard } from './ModalWrapper';
import { useTranslation } from '../hooks/useTranslation';

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 1.1rem;
  font-weight: 700;
`;

const Message = styled.p`
  margin: 0 0 24px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  white-space: pre-line;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
`;

const CancelBtn = styled(Button)`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.12);
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
`;

const ConfirmBtn = styled(Button)`
  background: ${props => props.$destructive ? '#d32f2f' : '#4CAF50'};
  color: white;
  &:hover { background: ${props => props.$destructive ? '#b71c1c' : '#43A047'}; }
`;

/**
 * ConfirmDialog — styled replacement for native confirm().
 *
 * Most call sites don't render this themselves: `useConfirm()` from
 * DialogProvider shows it and resolves a promise. Render it directly only
 * when the dialog's state belongs to the component (a pending action it
 * also lists, as ParticipantListSection does).
 *
 * Props:
 * - title: string (default t('common.confirm'))
 * - message: string
 * - confirmLabel: string (default t('common.ok'))
 * - cancelLabel: string (default t('common.cancel'))
 * - destructive: boolean — red confirm button
 * - onConfirm: () => void
 * - onCancel: () => void — also Escape
 */
export default function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const confirmRef = useRef(null);
  const resolvedTitle = title ?? t('common.confirm');
  const resolvedConfirm = confirmLabel ?? t('common.ok');
  const resolvedCancel = cancelLabel ?? t('common.cancel');

  // The focused button answers Enter by itself; a window keydown listener on
  // top of it fired onConfirm twice.
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <ModalWrapper visible onClose={onCancel} zIndex={1100} opacity={0.85} showCloseButton={false}>
      <ModalCard size="sm" onClick={(e) => e.stopPropagation()} style={{ padding: '28px 32px 24px' }}>
        <Title>{resolvedTitle}</Title>
        {message && <Message>{message}</Message>}
        <ButtonRow>
          <CancelBtn type="button" onClick={onCancel}>{resolvedCancel}</CancelBtn>
          <ConfirmBtn type="button" ref={confirmRef} $destructive={destructive} onClick={onConfirm}>
            {resolvedConfirm}
          </ConfirmBtn>
        </ButtonRow>
      </ModalCard>
    </ModalWrapper>
  );
}
